<template>
  <div class="video-container">
    <!-- 優化的 Loading 狀態 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <div class="gradient-circle"></div>
        
        <!-- 進度條 -->
        <div class="progress-bar-container">
          <div class="progress-text">
            {{ $t('loading') || '載入中' }}... {{ loadProgress }}%
          </div>
        </div>
        
      </div>
    </div>
    
    <!-- 錯誤訊息 -->
    <div v-if="error" class="error-message">
      {{ error }}
      <button class="btn btn-sm btn-light mt-2" @click="retryLoad">重試</button>
    </div>
    
    <!-- Canvas for rendering PNG sequence -->
    <canvas 
      ref="canvasRef"
      class="background-canvas"
      :class="{ 'opacity-50': loading }"
    ></canvas>
    
    <!-- Small Circle -->
    <div class="circle circle-small d-none d-sm-block">
      <div class="dot dot-0"></div>
      <div class="dot dot-315"></div>
    </div>
    
    <!-- Big Circle -->
    <div class="circle circle-big"></div>
    
    <!-- Info card 1 -->
    <div v-if="!loading && isInitialized" class="info-card card-1 d-none d-lg-inline-flex">
      <div class="card-content">
        <transition name="text-fade" mode="out-in">
          <p v-if="card1Texts[currentTextIndex.card1]" :key="currentTextIndex.card1" class="text-center animated-text" :class="{ en: $i18n.locale === 'en', zh: $i18n.locale === 'zh' }" v-html="$t(card1Texts[currentTextIndex.card1])"></p>
        </transition>
      </div>
    </div>
    
    <!-- Info card 2 -->
    <div v-if="!loading && isInitialized" class="info-card card-2">
      <div class="card-content">
        <transition name="text-fade" mode="out-in">
          <p v-if="card2Texts[currentTextIndex.card2]" :key="currentTextIndex.card2" class="text-center animated-text" :class="{ en: $i18n.locale === 'en', zh: $i18n.locale === 'zh' }" v-html="$t(card2Texts[currentTextIndex.card2])"></p>
        </transition>
      </div>
    </div>
    
    <!-- 前景文字內容層 -->
    <div class="content-overlay">
      <div class="container-fluid">
        <div class="row align-items-center min-vh-100">
          <div class="col-12 content-area">
            <div class="content-wrapper">
              <p v-if="$i18n.locale === 'zh'" class="h2 platform-title2">{{ $t('tainanCity') }}</p>
              <h1 class="platform-title" :class="{ en: $i18n.locale === 'en' }">{{ $t('culturalContentPlatform') }}</h1>
              <h4 v-if="$i18n.locale === 'zh'" class="en platform-subtitle">Tainan City Cultural Content<br>Development Platform</h4>
              <router-link to="/login" class="cta-button block-btn-hover">
                {{ $t('useNow') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll Down Arrow with Text -->
    <div class="scroll-down-container" :class="{ visible: isAtTop }">
      <div class="scroll-text">scroll</div>
      <div class="scroll-down-arrow">
        <i class="bi bi-arrow-down-short"></i>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

const canvasRef = ref(null)
const loading = ref(true)
const loadProgress = ref(0)
const error = ref('')
const isAtTop = ref(true)
const isInitialized = ref(false)

const currentTextIndex = ref({
  card1: 0,
  card2: 0
})

const card1Texts = [
  'infoCard1Text1',
  'infoCard1Text2'
]
const card2Texts = [
  'infoCard2Text1',
  'infoCard2Text2'
]

// ⭐ PNG 序列相關設定 - 全部預載優化
const TOTAL_FRAMES = 298
const FRAME_RATE = 30
const FRAME_DURATION = 1000 / FRAME_RATE
const IMAGE_PATH_PREFIX = '/videos/aiBall/ball'
const IMAGE_PATH_SUFFIX = '.png'

// ⭐ 優化配置：全部預載 + 永不刪除
const PRELOAD_SIZE = 298           // 載入全部
const WINDOW_SIZE = 298            // 保留全部
const MAX_CONCURRENT = 20          // 最大並發數
const BATCH_SIZE = 30              // 批次大小
const BATCH_INTERVAL = 0           // 移除延遲

let frameCache = new Map()
let currentFrame = 0
let animationFrameId = null
let lastFrameTime = 0
let textAnimationFrameId = null
let textAnimationStartTime = null
let isTextAnimationRunning = false
let isSequencePlaying = false
let resizeCleanup = null

// 快取縮放計算結果
let cachedDrawParams = null
let lastCanvasSize = { width: 0, height: 0 }

const throttle = (func, limit) => {
  let inThrottle
  return function() {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

const handleScrollThrottled = throttle(() => {
  const scrollY = window.scrollY
  isAtTop.value = scrollY <= 10
}, 100)

// 生成圖片路徑
const getImagePath = (frameNumber) => {
  const paddedNumber = String(frameNumber).padStart(5, '0')
  return `${IMAGE_PATH_PREFIX}${paddedNumber}${IMAGE_PATH_SUFFIX}`
}

// 檢查幀是否在快取中
const isFrameCached = (frameNumber) => {
  return frameCache.has(frameNumber)
}

// 獲取快取的幀
const getCachedFrame = (frameNumber) => {
  return frameCache.get(frameNumber)
}

// ⭐ 移除清理邏輯 - 永不刪除
const cleanupCache = () => {
  // 不做任何清理，保留所有已載入的圖片
  return
}

// 載入單張圖片
const loadSingleImage = (frameNumber) => {
  return new Promise((resolve, reject) => {
    if (isFrameCached(frameNumber)) {
      resolve(getCachedFrame(frameNumber))
      return
    }
    
    const img = new Image()
    const imagePath = getImagePath(frameNumber)
    
    img.onload = () => {
      frameCache.set(frameNumber, img)
      resolve(img)
    }
    
    img.onerror = () => {
      console.warn(`載入圖片失敗: ${imagePath}`)
      // 創建空白 canvas 作為後備
      const canvas = document.createElement('canvas')
      canvas.width = 1920
      canvas.height = 1080
      frameCache.set(frameNumber, canvas)
      resolve(canvas)
    }
    
    img.src = imagePath
  })
}

// ⭐ 並發載入池 - 最大化載入速度
const loadImagesWithConcurrency = async (frameNumbers, maxConcurrent = MAX_CONCURRENT) => {
  const queue = [...frameNumbers]
  const totalFrames = frameNumbers.length
  let completedFrames = 0
  
  const loadNext = async () => {
    while (queue.length > 0) {
      const frameNumber = queue.shift()
      
      try {
        await loadSingleImage(frameNumber)
        completedFrames++
        
        // 更新進度（只更新這批的進度）
        const batchProgress = Math.round((completedFrames / totalFrames) * 100)
        
        // 如果是首批（前60張），進度 0-20%
        if (totalFrames === 60) {
          loadProgress.value = Math.round(batchProgress * 0.2)
        } 
        // 如果是全部載入，進度 20-100%
        else if (totalFrames === 238) {
          loadProgress.value = 20 + Math.round(batchProgress * 0.8)
        }
        // 如果是一次載入全部
        else if (totalFrames === 298) {
          loadProgress.value = batchProgress
        }
        
      } catch (err) {
        console.error(`載入幀 ${frameNumber} 失敗:`, err)
      }
    }
  }
  
  // 啟動多個並發 workers
  const workers = Array(maxConcurrent).fill(null).map(() => loadNext())
  await Promise.all(workers)
}

// ⭐ 兩階段載入：快速啟動 + 背景完成
const preloadAllFrames = async () => {
  loading.value = true
  loadProgress.value = 0
  error.value = ''
  
  try {
    console.log(`開始兩階段載入全部 ${TOTAL_FRAMES} 張圖片...`)
    
    // ⭐ 階段 1: 載入前 60 張 (2秒內容) - 快速啟動
    console.log('階段 1: 載入前 60 張 (0-20%)...')
    const startTime = performance.now()
    
    await loadImagesWithConcurrency(
      Array.from({ length: 60 }, (_, i) => i + 1),
      15  // 前 60 張用 15 並發
    )
    
    const stage1Time = ((performance.now() - startTime) / 1000).toFixed(2)
    console.log(`階段 1 完成，耗時 ${stage1Time} 秒`)
    
    // 前 60 張載入完成，立即開始播放
    console.log('開始播放 2 秒循環')
    finishLoading()
    
    // ⭐ 階段 2: 背景載入剩餘 238 張 (20-100%)
    console.log('階段 2: 背景載入剩餘 238 張...')
    
    await loadImagesWithConcurrency(
      Array.from({ length: 238 }, (_, i) => i + 61),
      20  // 背景階段用 20 並發
    )
    
    const totalTime = ((performance.now() - startTime) / 1000).toFixed(2)
    console.log(`全部 298 張載入完成！總耗時 ${totalTime} 秒`)
    console.log(`快取大小: ${frameCache.size} 張`)
    
    loadProgress.value = 100
    
  } catch (err) {
    console.error('圖片載入失敗:', err)
    error.value = `圖片載入失敗: ${err.message}`
    loading.value = false
  }
}

// ⭐ 可選：一次全部載入（更簡單，但啟動較慢）
const preloadAllFramesSimple = async () => {
  loading.value = true
  loadProgress.value = 0
  error.value = ''
  
  try {
    console.log(`開始載入全部 ${TOTAL_FRAMES} 張圖片...`)
    const startTime = performance.now()
    
    await loadImagesWithConcurrency(
      Array.from({ length: TOTAL_FRAMES }, (_, i) => i + 1),
      20
    )
    
    const totalTime = ((performance.now() - startTime) / 1000).toFixed(2)
    console.log(`載入完成，耗時 ${totalTime} 秒`)
    
    finishLoading()
    
  } catch (err) {
    console.error('載入失敗:', err)
    error.value = err.message
    loading.value = false
  }
}

// 計算並快取繪製參數
const calculateDrawParams = (canvas, frame) => {
  const displayWidth = canvas.clientWidth
  const displayHeight = canvas.clientHeight
  
  // 檢查是否需要重新計算
  if (cachedDrawParams && 
      lastCanvasSize.width === displayWidth && 
      lastCanvasSize.height === displayHeight) {
    return cachedDrawParams
  }
  
  const canvasRatio = displayWidth / displayHeight
  const imageRatio = frame.width / frame.height
  
  let drawWidth, drawHeight, offsetX, offsetY
  
  if (canvasRatio > imageRatio) {
    drawWidth = displayWidth
    drawHeight = drawWidth / imageRatio
    offsetX = 0
    offsetY = (displayHeight - drawHeight) / 2
  } else {
    drawHeight = displayHeight
    drawWidth = drawHeight * imageRatio
    offsetX = (displayWidth - drawWidth) / 2
    offsetY = 0
  }
  
  const horizontalAdjust = 12
  
  cachedDrawParams = {
    drawWidth,
    drawHeight,
    offsetX: offsetX + horizontalAdjust,
    offsetY
  }
  
  lastCanvasSize = { width: displayWidth, height: displayHeight }
  
  return cachedDrawParams
}

// 初始化 Canvas
const initCanvas = () => {
  if (!canvasRef.value) return
  
  const canvas = canvasRef.value
  const container = canvas.parentElement
  
  const updateCanvasSize = () => {
    if (!canvas) return
    
    const displayWidth = container.clientWidth
    const displayHeight = container.clientHeight
    const dpr = window.devicePixelRatio || 1
    
    canvas.width = displayWidth * dpr
    canvas.height = displayHeight * dpr
    canvas.style.width = `${displayWidth}px`
    canvas.style.height = `${displayHeight}px`
    
    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)
    
    // 清除快取的繪製參數
    cachedDrawParams = null
    
    if (isSequencePlaying) {
      renderFrame()
    }
  }
  
  updateCanvasSize()
  
  const resizeHandler = throttle(updateCanvasSize, 200)
  window.addEventListener('resize', resizeHandler)
  
  return () => window.removeEventListener('resize', resizeHandler)
}

// 渲染當前幀
const renderFrame = () => {
  if (!canvasRef.value) return
  
  const frameNumber = currentFrame + 1
  const frame = getCachedFrame(frameNumber)
  
  if (!frame) {
    console.warn(`幀 ${frameNumber} 未載入`)
    return
  }
  
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')
  const displayWidth = canvas.clientWidth
  const displayHeight = canvas.clientHeight
  
  // 使用快取的繪製參數
  const params = calculateDrawParams(canvas, frame)
  
  // 清除畫布
  ctx.clearRect(0, 0, displayWidth, displayHeight)
  
  // 控制渲染品質
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  
  // 繪製圖片
  ctx.drawImage(frame, params.offsetX, params.offsetY, params.drawWidth, params.drawHeight)
}

// ⭐ 播放序列動畫 - 移除清理邏輯
const playSequence = async (timestamp) => {
  if (!isSequencePlaying) return
  
  if (!lastFrameTime) {
    lastFrameTime = timestamp
  }
  
  const elapsed = timestamp - lastFrameTime
  
  if (elapsed >= FRAME_DURATION) {
    // 處理掉幀
    const framesPassed = Math.floor(elapsed / FRAME_DURATION)
    currentFrame = (currentFrame + framesPassed) % TOTAL_FRAMES
    
    const frameNumber = currentFrame + 1
    
    // 如果幀未載入（不應該發生，因為全部預載）
    if (!isFrameCached(frameNumber)) {
      console.warn(`幀 ${frameNumber} 不在快取中`)
    }
    
    renderFrame()
    
    // ⭐ 移除清理邏輯
    // 因為我們要永久保留所有圖片
    
    lastFrameTime = timestamp - (elapsed % FRAME_DURATION)
  }
  
  animationFrameId = requestAnimationFrame(playSequence)
}

// 開始播放
const startSequence = () => {
  if (isSequencePlaying || frameCache.size === 0) return
  
  console.log('開始播放序列動畫')
  isSequencePlaying = true
  lastFrameTime = 0
  animationFrameId = requestAnimationFrame(playSequence)
}

// 停止播放
const stopSequence = () => {
  isSequencePlaying = false
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
  lastFrameTime = 0
}

// 完成載入
const finishLoading = () => {
  console.log('完成載入處理')
  loading.value = false
  isInitialized.value = true
  error.value = ''
  
  resizeCleanup = initCanvas()
  renderFrame()
  startSequence()
  
  setTimeout(() => {
    if (isInitialized.value) {
      startTextAnimation()
    }
  }, 100)
}

// 重試載入
const retryLoad = () => {
  error.value = ''
  
  // 清理現有快取
  for (const [, img] of frameCache) {
    if (img && img.src) {
      img.src = ''
    }
  }
  frameCache.clear()
  
  currentFrame = 0
  cachedDrawParams = null
  
  // 重新載入
  // preloadAllFrames()
  // 或使用簡單版本（一次全部載入）
  preloadAllFramesSimple()
}

// 文字動畫
const startTextAnimation = () => {
  if (isTextAnimationRunning || !isInitialized.value) return
  
  if (!card1Texts || !card2Texts || card1Texts.length === 0 || card2Texts.length === 0) {
    console.warn('Card text arrays not ready')
    return
  }
  
  isTextAnimationRunning = true
  textAnimationStartTime = performance.now()
  
  const CARD1_INTERVAL = 5000
  const CARD2_INTERVAL = 5000
  
  const animationLoop = (currentTime) => {
    if (!isTextAnimationRunning) return
    
    const elapsed = currentTime - textAnimationStartTime
    
    const card1CycleProgress = elapsed % CARD1_INTERVAL
    const newCard1Index = Math.floor(elapsed / CARD1_INTERVAL) % card1Texts.length
    if (currentTextIndex.value.card1 !== newCard1Index && card1CycleProgress < 50) {
      currentTextIndex.value.card1 = newCard1Index
    }
    
    const card2CycleProgress = elapsed % CARD2_INTERVAL
    const newCard2Index = Math.floor(elapsed / CARD2_INTERVAL) % card2Texts.length
    if (currentTextIndex.value.card2 !== newCard2Index && card2CycleProgress < 50) {
      currentTextIndex.value.card2 = newCard2Index
    }
    
    textAnimationFrameId = requestAnimationFrame(animationLoop)
  }
  
  textAnimationFrameId = requestAnimationFrame(animationLoop)
}

const stopTextAnimation = () => {
  isTextAnimationRunning = false
  if (textAnimationFrameId) {
    cancelAnimationFrame(textAnimationFrameId)
    textAnimationFrameId = null
  }
}

const resetTextAnimation = () => {
  stopTextAnimation()
  currentTextIndex.value = { card1: 0, card2: 0 }
  textAnimationStartTime = null
}

// 清理函數
const cleanup = () => {
  isInitialized.value = false
  stopTextAnimation()
  stopSequence()
  window.removeEventListener('scroll', handleScrollThrottled)
  
  if (resizeCleanup) {
    resizeCleanup()
    resizeCleanup = null
  }
  
  // 清理所有快取的圖片
  for (const [, img] of frameCache) {
    if (img && img.src) {
      img.src = ''
    }
  }
  frameCache.clear()
  cachedDrawParams = null
}

onMounted(() => {
  console.log('HomeSpline mounted (PNG Sequence - Full Preload Optimized)')
  console.log(`配置: PRELOAD_SIZE=${PRELOAD_SIZE}, MAX_CONCURRENT=${MAX_CONCURRENT}`)
  
  window.addEventListener('scroll', handleScrollThrottled, { passive: true })
  
  // 開始載入
  // preloadAllFrames()
  // 或使用簡單版本（一次全部載入）
  preloadAllFramesSimple()
})

onBeforeUnmount(() => {
  cleanup()
})

watch(() => document.visibilityState, (newState) => {
  if (newState === 'visible') {
    if (!isTextAnimationRunning && isInitialized.value) {
      startTextAnimation()
    }
    if (!isSequencePlaying && frameCache.size > 0) {
      startSequence()
    }
  } else {
    stopTextAnimation()
    stopSequence()
  }
})

defineExpose({
  startAnimation: startTextAnimation,
  stopAnimation: stopTextAnimation,
  resetAnimation: resetTextAnimation,
  startSequence,
  stopSequence,
  getCacheSize: () => frameCache.size,
  getLoadProgress: () => loadProgress.value
})
</script>

<style scoped>
/* 基礎樣式 */
.video-container { position: relative; width: 100%; height: 100vh; overflow: hidden; background: transparent; }
.background-canvas { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 100%; height: 100%; display: block; transition: opacity 0.3s ease; z-index: 0; background: none; }
.circle { position: absolute; top: 50%; left: 48.5%; transform: translate(-50%, -50%); width: 50vw; height: 50vw; border: 3px solid white; border-radius: 50%; background: transparent; z-index: 5; }
.circle.circle-small { width: 50vw; height: 50vw; opacity: 0.4; }
.circle.circle-big { width: 80vw; height: 80vw; opacity: 0.2; }
.dot { position: absolute; transform: translate(-50%, -50%); width: 24px; height: 24px; background: white; border-radius: 50%; opacity: 1; }
.dot.dot-0 { top: 50%; left: 100%; }
.dot.dot-315 { top: 85.4%; left: 85.4%; }
.info-card { position: absolute; top: 0; left: 0; transform: translate(-100%,-50%); height: 12%; width: 22%; display: inline-flex; justify-content: center; align-items: center; white-space: nowrap; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(20px); padding: 15px 15px; border: 1px solid rgba(255, 255, 255, 0.2); margin-bottom: 20px; border-radius: 45px; box-shadow: 0 14px 14px 0 rgba(0, 0, 0, 0.12); z-index: 15; }
.info-card.card-1 { top: 50%; left: 97%; }
.info-card.card-2 { top: 80%; left: 90%; }
.content-overlay { position: relative; z-index: 10; width: 100%; min-height: 100vh; }
.content-area { padding: 80px 60px; max-height: 100vh; }
.content-wrapper { max-width: 800px; }
.platform-title2 { font-size: 55px; }
.platform-title { margin-bottom: 1rem; }
.platform-subtitle { font-size: 28px; font-weight: 700; color: #ff7729; letter-spacing: 2px; }
.cta-button { background-color: #000; color: white; border: none; display: inline-block; margin-top: 4rem; padding: 15px 35px; font-size: 26px; font-weight: 600; letter-spacing: 3px; border-radius: 50px; cursor: pointer; text-decoration: none; transition: all 0.3s ease; }
.cta-button:hover { background-color: #34495e; transform: translateY(-2px); }
.card-content { min-height: 40px; display: flex; align-items: center; justify-content: center; }
.card-content .zh { font-size: 1.3vw; color: #2c3e50; font-weight: 400; line-height: 1.5; letter-spacing: 2px; }
.card-content .en { font-size: 1vw; color: #2c3e50; font-weight: 500; line-height: 1.5; letter-spacing: 1px; }
.animated-text { margin: 0; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center; }
.text-fade-enter-active, .text-fade-leave-active { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
.text-fade-enter-from { opacity: 0; transform: translateY(15px) scale(0.95); }
.text-fade-leave-to { opacity: 0; transform: translateY(-15px) scale(0.95); }
.text-fade-enter-to, .text-fade-leave-from { opacity: 1; transform: translateY(0) scale(1); }

/* Loading 覆蓋層 - 優化版 */
.loading-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.9); display: flex; flex-direction: column; justify-content: center; align-items: center; color: white; z-index: 100; }
.loading-content { display: flex; flex-direction: column; align-items: center; gap: 30px; padding: 20px; }

/* 漸層圓圈 Loading 動畫 */
.gradient-circle { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(45deg, #FF6B6B, #ff7729, #fff, #999); background-size: 200% 200%; animation: gradientRotate 2s ease-in-out infinite; position: relative; }
.gradient-circle::before { content: ''; position: absolute; top: 50%; left: 50%; width: 50px; height: 50px; background: rgba(0, 0, 0, 0.9); border-radius: 50%; transform: translate(-50%, -50%); }

/* 進度條容器 */
.progress-bar-container { width: 320px; text-align: center; }
.progress-text { color: white; font-size: 20px; font-weight: 600; margin-bottom: 4px; letter-spacing: 1px; }

/* Fade 過渡動畫 */
.fade-enter-active, .fade-leave-active { transition: opacity 0.5s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* 錯誤訊息 */
.error-message { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(220, 53, 69, 0.95); color: white; padding: 30px; border-radius: 12px; text-align: center; z-index: 100; max-width: 80%; word-wrap: break-word; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); }
.opacity-50 { opacity: 0.5; }

/* Scroll Down 指示器 */
.scroll-down-container { position: absolute; bottom: 0; left: 48.5%; transform: translateX(-50%); z-index: 20; display: flex; flex-direction: column; align-items: center; color: #ffffff; cursor: pointer; opacity: 0; visibility: hidden; transition: all 0.3s ease; pointer-events: none; }
.scroll-down-container.visible { opacity: 0.5; visibility: visible; pointer-events: auto; }
.scroll-down-container.visible:hover { opacity: 0.8; }
.scroll-text { font-size: 14px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; margin-bottom: -10px; animation: simpleUpDown 2s ease-in-out infinite; }
.scroll-down-arrow { font-size: 40px; animation: simpleUpDown 2s ease-in-out infinite; animation-delay: 0.1s; }

/* 性能優化 */
.info-card, .scroll-down-container, .animated-text { will-change: transform, opacity; }
.text-fade-enter-active, .text-fade-leave-active, .simpleUpDown { transform: translateZ(0); }

/* 動畫關鍵幀 */
@keyframes simpleUpDown { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes gradientRotate { 0% { background-position: 0% 50%; transform: rotate(0deg); } 50% { background-position: 100% 50%; transform: rotate(180deg); } 100% { background-position: 0% 50%; transform: rotate(360deg); } }
@keyframes progressShine { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes fadeInOut { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }

/* 減少動畫 */
@media (prefers-reduced-motion: reduce) { 
  .animated-text, .text-fade-enter-active, .text-fade-leave-active { transition: none; } 
  .text-fade-enter-from, .text-fade-leave-to { transform: none; }
  .gradient-circle, .progress-fill { animation: none; }
}

/* 平板響應式 */
@media (max-width: 992px) { 
  .circle.circle-small { width: 85vw; height: 85vw; } 
  .circle.circle-big { width: 110vw; height: 110vw; } 
  .content-area { padding: 60px 30px; } 
  .platform-title2 { font-size: 45px; line-height: 40px; } 
  .platform-title { font-size: 50px; letter-spacing: 2px; } 
  .platform-title.en { font-size: 36px; font-weight: 900; line-height: 50px; letter-spacing: 3px; } 
  .platform-subtitle { font-size: 25px; } 
  .cta-button { margin-top: 3.5rem; } 
  .info-card { width: 40%; height: 8%; } 
  .card-content .zh { font-size: 1.8vw; } 
  .card-content .en { font-size: 1.6vw; } 
  .scroll-down-container { bottom: -10px; } 
  .scroll-text { font-size: 13px; } 
  .scroll-down-arrow { font-size: 36px; }
  .progress-bar-container { width: 280px; }
  .progress-text { font-size: 18px; }
}

/* 手機響應式 */
@media (max-width: 768px) { 
  .background-canvas { top: 40%; } 
  .circle { top: 40%; } 
  .circle.circle-small { width: 110vw; height: 110vw; } 
  .circle.circle-big { width: 160vw; height: 160vw; } 
  .info-card.card-2 { top: 83%; left: 50%; transform: translateX(-50%); } 
  .info-card.card-2 p { font-size: 15px; } 
  .row.align-items-center { align-items: flex-end !important; padding-bottom: 200px; } 
  .content-area { padding: 40px 20px; } 
  .platform-title2 { font-size: 35px; font-weight: 700; line-height: 35px; letter-spacing: 2px; } 
  .platform-title { font-size: 35px; font-weight: 700; line-height: 40px; letter-spacing: 2px; } 
  .platform-title.en { font-size: 36px; font-weight: 800; line-height: 35px; letter-spacing: 2px; } 
  .platform-subtitle { font-size: 1rem; } 
  .cta-button { margin-top: 2.5rem; padding: 12px 30px; font-size: 20px; letter-spacing: 3px; } 
  .info-card { min-width: 260px; width: 60%; padding: 15px 20px; margin-bottom: 15px; } 
  .card-content { font-size: 14px; min-height: 35px; } 
  .scroll-down-container { bottom: -10px; } 
  .scroll-text { font-size: 12px; } 
  .scroll-down-arrow { font-size: 32px; }
  .gradient-circle { width: 50px; height: 50px; }
  .gradient-circle::before { width: 40px; height: 40px; }
  .progress-bar-container { width: 260px; }
  .progress-text { font-size: 16px; }
}
</style>